import React, { useState, useEffect } from "react";
import {
  GetMetadataAdmin,
  GetMetadataStatsAdmin,
} from "../../api-requests/Get";
import {
  UpdateMetadataReview,
  DeleteMetadata,
} from "../../api-requests/Updates";
import { getBearerHeaders } from "../../helpers/GetBearerHeaders";
import PageLoading from "../../components/PageLoading";

interface MetadataItem {
  id: string;
  itemId: string;
  categoryId: string;
  confidenceScore: number;
  sourceType: "ai_generated" | "manual" | "hybrid";
  reviewStatus: "pending" | "approved" | "rejected";
  createdAt: string;
  updatedAt: string;
  Item: {
    id: string;
    name: string;
    imagePath?: string;
    Category: {
      id: string;
      name: string;
    };
  };
  parsedMetadata: {
    primaryEra: string | null;
    secondaryEra: string | null;
    region: string | null;
    styles: string[];
    mainstreamLevel: number;
    lyricismLevel: number;
    influenceLevel: number;
    tags: string[];
  };
}

interface MetadataStats {
  pending: number;
  approved: number;
  rejected: number;
  total: number;
  avgConfidence: string;
}

const MetadataAdmin: React.FC = () => {
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [stats, setStats] = useState<MetadataStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<MetadataItem | null>(null);
  const [editingMetadata, setEditingMetadata] = useState<string>("");
  const [filters, setFilters] = useState({
    reviewStatus: "all",
    sourceType: "all",
    page: 1,
    limit: 20,
  });

  const loadMetadata = async () => {
    setLoading(true);
    try {
      const [metadataResponse, statsResponse] = await Promise.all([
        GetMetadataAdmin(filters, getBearerHeaders()),
        GetMetadataStatsAdmin(getBearerHeaders()),
      ]);

      if (metadataResponse.success) {
        setMetadata(metadataResponse.data.metadata);
      }

      if (statsResponse.success) {
        setStats(statsResponse.data);
      }
    } catch (error) {
      console.error("Failed to load metadata:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMetadata();
  }, [filters]);

  const handleReviewStatusUpdate = async (
    id: string,
    reviewStatus: string,
    metadataJson?: string,
  ) => {
    try {
      const updateData: { reviewStatus: string; metadataJson?: string } = {
        reviewStatus,
      };
      if (metadataJson) {
        updateData.metadataJson = metadataJson;
      }

      const response = await UpdateMetadataReview(
        id,
        updateData,
        getBearerHeaders(),
      );

      if (response.success) {
        // Update local state
        setMetadata((prev) =>
          prev.map((item) =>
            item.id === id ? { ...item, ...response.data } : item,
          ),
        );
        setSelectedItem(null);
        setEditingMetadata("");
        loadMetadata(); // Reload to update stats
      }
    } catch (error) {
      console.error("Failed to update review status:", error);
    }
  };

  const handleDeleteMetadata = async (id: string) => {
    if (!confirm("Are you sure you want to delete this metadata item?")) return;

    try {
      const response = await DeleteMetadata(id, getBearerHeaders());
      if (response.success) {
        setMetadata((prev) => prev.filter((item) => item.id !== id));
        setSelectedItem(null);
        loadMetadata(); // Reload to update stats
      }
    } catch (error) {
      console.error("Failed to delete metadata:", error);
    }
  };

  const openEditor = (item: MetadataItem) => {
    setSelectedItem(item);
    setEditingMetadata(JSON.stringify(item.parsedMetadata, null, 2));
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getSourceTypeColor = (sourceType: string) => {
    switch (sourceType) {
      case "ai_generated":
        return "text-blue-600";
      case "manual":
        return "text-green-600";
      case "hybrid":
        return "text-purple-600";
      default:
        return "text-gray-600";
    }
  };

  if (loading && !stats) {
    return <PageLoading />;
  }

  return (
    <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 mt-6">
      {/* Stats Section */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-gray-800 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-400">Total</h3>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-400">Pending</h3>
            <p className="text-2xl font-bold text-yellow-400">
              {stats.pending}
            </p>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-400">Approved</h3>
            <p className="text-2xl font-bold text-green-400">
              {stats.approved}
            </p>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-400">Rejected</h3>
            <p className="text-2xl font-bold text-red-400">{stats.rejected}</p>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-400">
              Avg Confidence
            </h3>
            <p className="text-2xl font-bold text-blue-400">
              {(parseFloat(stats.avgConfidence) * 100).toFixed(1)}%
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-800 p-4 rounded-lg mb-6">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Status
            </label>
            <select
              value={filters.reviewStatus}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  reviewStatus: e.target.value,
                  page: 1,
                })
              }
              className="bg-gray-700 text-white rounded px-3 py-2 text-sm"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Source
            </label>
            <select
              value={filters.sourceType}
              onChange={(e) =>
                setFilters({ ...filters, sourceType: e.target.value, page: 1 })
              }
              className="bg-gray-700 text-white rounded px-3 py-2 text-sm"
            >
              <option value="all">All Sources</option>
              <option value="ai_generated">AI Generated</option>
              <option value="manual">Manual</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
        </div>
      </div>

      {/* Metadata Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Artist
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Metadata
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Confidence
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-700">
              {metadata.map((item) => (
                <tr key={item.id} className="hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <img
                          className="h-10 w-10 rounded-full object-cover"
                          src={item.Item.imagePath || "/default-avatar.png"}
                          alt={item.Item.name}
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-200">
                          {item.Item.name}
                        </div>
                        <div className="text-sm text-gray-400">
                          {item.Item.Category.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-200">
                      {item.parsedMetadata.primaryEra && (
                        <span className="inline-block bg-blue-600 text-white px-2 py-1 rounded text-xs mr-1 mb-1">
                          {item.parsedMetadata.primaryEra}
                        </span>
                      )}
                      {item.parsedMetadata.region && (
                        <span className="inline-block bg-green-600 text-white px-2 py-1 rounded text-xs mr-1 mb-1">
                          {item.parsedMetadata.region.replace("_", " ")}
                        </span>
                      )}
                      {item.parsedMetadata.styles
                        .slice(0, 2)
                        .map((style, idx) => (
                          <span
                            key={idx}
                            className="inline-block bg-purple-600 text-white px-2 py-1 rounded text-xs mr-1 mb-1"
                          >
                            {style}
                          </span>
                        ))}
                    </div>
                    <div className="text-xs text-gray-400 mt-2">
                      M:{item.parsedMetadata.mainstreamLevel} L:
                      {item.parsedMetadata.lyricismLevel} I:
                      {item.parsedMetadata.influenceLevel}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-200">
                      {(item.confidenceScore * 100).toFixed(1)}%
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(item.reviewStatus)}`}
                    >
                      {item.reviewStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`text-sm font-medium ${getSourceTypeColor(item.sourceType)}`}
                    >
                      {item.sourceType.replace("_", " ")}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => openEditor(item)}
                      className="text-blue-400 hover:text-blue-300 mr-3"
                    >
                      Review
                    </button>
                    <button
                      onClick={() => handleDeleteMetadata(item.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Review Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">
                  Review {selectedItem.Item.name} Metadata
                </h2>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Current Metadata Display */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">
                    Current Metadata
                  </h3>
                  <div className="bg-gray-900 p-4 rounded">
                    <div className="space-y-2 text-sm">
                      <div>
                        <strong className="text-gray-400">Era:</strong>{" "}
                        {selectedItem.parsedMetadata.primaryEra || "N/A"}
                      </div>
                      <div>
                        <strong className="text-gray-400">Region:</strong>{" "}
                        {selectedItem.parsedMetadata.region || "N/A"}
                      </div>
                      <div>
                        <strong className="text-gray-400">Styles:</strong>{" "}
                        {selectedItem.parsedMetadata.styles.join(", ") || "N/A"}
                      </div>
                      <div>
                        <strong className="text-gray-400">Mainstream:</strong>{" "}
                        {selectedItem.parsedMetadata.mainstreamLevel}/10
                      </div>
                      <div>
                        <strong className="text-gray-400">Lyricism:</strong>{" "}
                        {selectedItem.parsedMetadata.lyricismLevel}/10
                      </div>
                      <div>
                        <strong className="text-gray-400">Influence:</strong>{" "}
                        {selectedItem.parsedMetadata.influenceLevel}/10
                      </div>
                      <div>
                        <strong className="text-gray-400">Tags:</strong>{" "}
                        {selectedItem.parsedMetadata.tags.join(", ") || "N/A"}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="text-sm text-gray-400">
                      Confidence:{" "}
                      {(selectedItem.confidenceScore * 100).toFixed(1)}% |
                      Source: {selectedItem.sourceType} | Status:{" "}
                      {selectedItem.reviewStatus}
                    </div>
                  </div>
                </div>

                {/* Edit Metadata */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">
                    Edit Metadata JSON
                  </h3>
                  <textarea
                    value={editingMetadata}
                    onChange={(e) => setEditingMetadata(e.target.value)}
                    className="w-full h-64 bg-gray-900 text-white p-3 rounded font-mono text-sm"
                    placeholder="Edit the metadata JSON..."
                  />
                  <p className="text-xs text-gray-400 mt-2">
                    Editing will mark the source as "hybrid". Leave unchanged to
                    keep original values.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-700">
                <button
                  onClick={() =>
                    handleReviewStatusUpdate(selectedItem.id, "rejected")
                  }
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded"
                >
                  Reject
                </button>
                <button
                  onClick={() =>
                    handleReviewStatusUpdate(selectedItem.id, "pending")
                  }
                  className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded"
                >
                  Mark Pending
                </button>
                <button
                  onClick={() =>
                    handleReviewStatusUpdate(
                      selectedItem.id,
                      "approved",
                      editingMetadata !==
                        JSON.stringify(selectedItem.parsedMetadata, null, 2)
                        ? editingMetadata
                        : undefined,
                    )
                  }
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded"
                >
                  Approve
                  {editingMetadata !==
                  JSON.stringify(selectedItem.parsedMetadata, null, 2)
                    ? " with Changes"
                    : ""}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetadataAdmin;
